import type { TransactionCurrency } from '@contracts/payment/Currency';
import { roundToTwoDecimalsString } from '@contracts/utils/RoundingAmount';
import Alert from '@mui/material/Alert';
import { Divider } from '@src/common/components/Divider';
import { MoneyInput } from '@src/common/components/MoneyInput';
import { Stack } from '@src/common/components/Stack';
import { Typography } from '@src/common/components/Typography';
import { css } from '@src/common/styles/Styles';
import { theme } from '@src/common/styles/Theme';
import type { CompletePermitSchemaType } from '@src/permits/completePermit/fields/CompletePermitFields';
import {
  JurisdictionFeeField,
  ServiceFeeField,
} from '@src/permits/completePermit/fields/CompletePermitFields';
import type { FormState, UseFormWatch } from 'react-hook-form';
import type { UseFormRegister } from 'react-hook-form/dist/types/form';
import { useTranslation } from 'react-i18next';

interface Props {
  register: UseFormRegister<CompletePermitSchemaType>;
  formState: FormState<CompletePermitSchemaType>;
  watch: UseFormWatch<CompletePermitSchemaType>;
  currency: TransactionCurrency;
  readOnly?: boolean;
}

export const PermitFees = ({
  register,
  formState,
  watch,
  currency,
  readOnly,
}: Props) => {
  const { t } = useTranslation();

  const jurisdictionFee = +(watch(JurisdictionFeeField.fieldName) ?? 0);
  const serviceFee = +(watch(ServiceFeeField.fieldName) ?? 0);
  const total = roundToTwoDecimalsString(jurisdictionFee + serviceFee);

  const getLabelAndValue = (fieldName: 'jurisdictionFee' | 'serviceFee') => (
    <div className={grid}>
      <div className={gridItem}>
        <Typography variant="body2" className={labelStyle}>
          {t(`fields.${fieldName}`)}
        </Typography>
      </div>

      <div className={gridItem}>
        <MoneyInput
          fieldName={fieldName}
          register={register}
          errors={formState.errors}
          readOnly={readOnly}
        />
      </div>
    </div>
  );

  const givenGridDivider = () => (
    <div className={grid}>
      <div className={gridItem}>
        <Divider sx={{ margin: '20px 0' }} />
      </div>
    </div>
  );

  return (
    <Stack gap={theme.spacing['8']}>
      <Alert severity="info">
        {t('permitCurrency', {
          currency,
        })}
      </Alert>

      <div>
        {getLabelAndValue('jurisdictionFee')}

        {givenGridDivider()}

        {getLabelAndValue('serviceFee')}

        {givenGridDivider()}

        <div className={grid}>
          <div className={gridItem}>
            <Typography variant="body2" className={labelStyle}>
              {t('fields.total')}
            </Typography>
          </div>
          <div className={gridItem}>
            <Typography>${total.toString()}</Typography>
          </div>
        </div>
      </div>
    </Stack>
  );
};

const labelStyle = css(() => ({
  fontWeight: '600',
}));

const grid = css(() => ({
  display: 'flex',
  alignItems: 'center',
}));

const gridItem = css(() => ({
  flex: 1,
}));
